"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Loader } from "lucide-react";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[var(--color-border)] bg-white">
        <p className="font-sans text-2xl text-[var(--color-text-dark)] mb-3" style={{
        letterSpacing: "-0.02em"
      }}>
          Thank you! <span className="italic" style={{
          color: "var(--color-accent)"
        }}>Your registration is received.</span>
        </p>
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[var(--color-text-dark)] text-[var(--color-text-dark)] hover:bg-[var(--color-text-dark)] hover:text-white rounded-lg uppercase tracking-[0.08em] text-xs font-semibold">
          Register Another Buyer
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem data-index={0}>
            <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="h-12 bg-white border-0 border-b border-[var(--color-border)] px-3 focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200 text-[var(--color-text-dark)]" />
            </FormControl>
            <FormDescription className="text-xs text-gray-500">
              Enter the primary contact name.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" {...field} className="h-12 bg-white border-0 border-b border-[var(--color-border)] px-3 focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200 text-[var(--color-text-dark)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="h-12 bg-white border-0 border-b border-[var(--color-border)] px-3 focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200 text-[var(--color-text-dark)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} className="h-12 bg-white border-0 border-b border-[var(--color-border)] px-3 focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200 text-[var(--color-text-dark)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="industry" rules={{
        required: "Industry is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                Industry
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="h-12 w-full bg-white border-0 border-b border-[var(--color-border)] px-3 font-sans text-sm text-[var(--color-text-dark)] focus-visible:ring-0 focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Logistics">Logistics</SelectItem>
                    <SelectItem value="Technology">Technology</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
              Additional Notes
            </FormLabel>
            <FormControl>
              <textarea placeholder="e.g., specific sourcing requirements" rows={3} {...field} className="flex w-full bg-white border-0 border-b border-[var(--color-border)] px-3 py-2 text-sm outline-none focus-visible:border-[var(--color-primary)] focus-visible:border-b-2 transition-colors duration-200 text-[var(--color-text-dark)] placeholder:text-gray-400 resize-none" />
            </FormControl>
            <FormDescription className="text-xs text-gray-500">
              Optional: provide details on your procurement needs.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-500 bg-red-50">
          <p className="text-sm text-red-700">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-lg uppercase tracking-[0.08em] text-xs font-semibold group bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)]/90 shadow-sm">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Submit Registration
            <ArrowRight className="ml-1 size-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
          </>}
      </Button>
    </Form>;
}
export default function Buyerregistrationform() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1045" id="buyerregistrationform" className="bg-white text-[var(--color-text-dark)] py-28 md:py-40 overflow-hidden relative">
      <div ref={ref} className="max-w-[1320px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6 relative" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-sans text-[0.75rem] font-medium uppercase tracking-[0.18em] text-gray-500">
                § 06 — Join KTEX / Buyer Registration
              </span>
            </div>

            <div className="flex items-center gap-2 mb-10">
              <div className="h-px flex-1 max-w-[120px]" style={{
              backgroundColor: "var(--color-border)"
            }} />
              <div className="size-1 rounded-lg" style={{
              backgroundColor: "var(--color-accent)"
            }} />
            </div>

            <div className="relative">
              <span aria-hidden="true" className="absolute font-sans italic pointer-events-none select-none" style={{
              fontSize: "clamp(8rem, 16vw, 14rem)",
              color: "var(--color-primary)",
              opacity: 0.05,
              top: "-40px",
              left: "-20px",
              lineHeight: 1,
              fontWeight: 700
            }}>
                KT
              </span>

              <h2 className="font-sans text-[var(--color-text-dark)] relative" style={{
              fontSize: "clamp(2.25rem, 4.6vw, 4rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.02
            }}>
                Become a{" "}
                <span className="italic" style={{
                color: "var(--color-accent)",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-8px)",
                transition: "opacity 700ms 200ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms 200ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                display: "inline-block"
              }}>
                  Verified Buyer
                </span>
              </h2>
            </div>

            <p className="mt-8 text-gray-600 max-w-[420px]" style={{
            fontSize: "clamp(1.05rem, 1.1vw, 1.2rem)",
            lineHeight: 1.7
          }}>
              Join our global network of verified buyers and gain access to premium trade opportunities and streamlined procurement.
            </p>

            <div className="mt-12 space-y-3 max-w-[420px]">
              <div className="flex items-center gap-3">
                <div className="h-px w-6" style={{
                backgroundColor: "var(--color-border)"
              }} />
                <span className="font-sans text-xs uppercase tracking-[0.12em] text-gray-500">
                  Global Trade Network
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-px w-6" style={{
                backgroundColor: "var(--color-border)"
              }} />
                <span className="font-sans text-xs uppercase tracking-[0.12em] text-gray-500">
                  Secure Procurement
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 700ms 120ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms 120ms cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
            <div className="relative p-3" style={{
            border: "1px solid var(--color-border)"
          }}>
              <div className="relative bg-white" style={{
              border: "1px solid var(--color-border)"
            }}>
                <div className="p-5 md:p-10">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-sans text-[0.7rem] uppercase tracking-[0.18em] text-gray-500">
                      Registration
                    </span>
                    <span className="font-sans text-[0.7rem] tracking-[0.18em] text-gray-500">
                      KTEX-B
                    </span>
                  </div>

                  <h3 className="font-sans text-[var(--color-text-dark)] mb-1" style={{
                  fontSize: "clamp(1.5rem, 2vw, 2rem)",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.15
                }}>
                    Buyer Application
                  </h3>

                  <div className="flex items-center gap-2 mb-8">
                    <div className="h-px flex-1" style={{
                    backgroundColor: "var(--color-border)"
                  }} />
                    <div className="size-1 rounded-lg" style={{
                    backgroundColor: "var(--color-accent)"
                  }} />
                    <div className="h-px flex-1" style={{
                    backgroundColor: "var(--color-border)"
                  }} />
                  </div>

                  <FormProvider formKey="buyer_form" formId="buyer_form" sectionName="buyerregistrationform" defaultValues={{
                  full_name: "",
                  email: "",
                  phone: "",
                  company_name: "",
                  industry: "",
                  special_requests: ""
                }} validationRules={{
                  full_name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  company_name: {
                    required: true
                  },
                  industry: {
                    required: true
                  }
                }} submitText="Submit Registration" successMessage="Your application has been submitted. Our team will review your details and contact you shortly." errorMessage="We encountered an error submitting your application. Please try again.">
                    <ReservationForm />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}